'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import dynamic from 'next/dynamic'
import type { ReactNode } from 'react'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import { TYPE } from '@/lib/ui/typography'

/**
 * The one empty state for every signed-in surface, in the marketing
 * site's language (mk-* tokens: medium-weight ink titles, warm body copy,
 * pill actions, the golden-hour sky). The whole design space is
 * size × reason; nothing else is configurable. Size comes from where it
 * renders, reason from the data. Copy is always the caller's catalog keys.
 * Designs: Paper "Brew · Empty states — audit, system, redesign" → S0.
 */

/** page: a route body or full-screen gate · panel: a table, card, tab, sheet
 * or dialog pane · inline: a popover, picker, ⌘K, chart or sub-section. */
export type EmptyStateSize = 'page' | 'panel' | 'inline'

/** empty: nothing yet · no-results: a search or filter hides the data ·
 * done: the queue is clear (good news, shown with a status line). */
export type EmptyStateReason = 'empty' | 'no-results' | 'done'

export type EmptyStateAction = { label: ReactNode } & (
  | { href: string; onClick?: never; disabled?: never; disabledReason?: never }
  | {
      onClick: () => void
      href?: never
      /** A gated action (plan, role): shown but inert, with the reason as its tooltip. */
      disabled?: boolean
      disabledReason?: string
    }
)

type EmptyStateBaseProps = {
  title: ReactNode
  description?: ReactNode
  reason?: EmptyStateReason
  /** One action. Inline sizes render it as an accent link. */
  action?: EmptyStateAction
  className?: string
}

type EmptyStateBlockProps = EmptyStateBaseProps & {
  /** A quiet text action beside the primary one. */
  secondaryAction?: EmptyStateAction
  /** The small line above the title: "Checked 2 min ago" for done (with a
   * green dot), "Not found" for the NotFoundState preset. */
  kicker?: ReactNode
  /** Rendered between the copy and the actions (a brand list, a copy chip). */
  children?: ReactNode
}

/** A starter request: the short line the box shows, and what it sends. */
export type EmptyStatePrompt = { label: string; value: string }

/** First-run pages lead with the sky: Brew's prompt box cycling real
 * requests, sent to chat on submit. Outcome previews use `media` instead. */
type PageVisual =
  | {
      prompts: ReadonlyArray<EmptyStatePrompt>
      onPrompt: (value: string) => void
      media?: never
    }
  | { media: ReactNode; prompts?: never; onPrompt?: never }
  | { prompts?: never; onPrompt?: never; media?: never }

export type EmptyStateProps =
  | (EmptyStateBlockProps & { size: 'page' } & PageVisual)
  | (EmptyStateBlockProps & { size?: 'panel' })
  | (EmptyStateBaseProps & { size: 'inline' })

/** Every size-dependent value, in one place. */
const SIZE = {
  page: {
    root: 'gap-8 px-6 py-16 md:py-24',
    copy: 'max-w-[480px] gap-3',
    title: 'font-medium text-2xl text-mk-ink tracking-[-0.02em] md:text-3xl',
    description: 'text-base/relaxed text-mk-body tracking-[-0.01em]',
    button: 'lg',
  },
  panel: {
    root: 'gap-5 px-6 py-10',
    copy: 'max-w-[300px] gap-1.5',
    title: 'font-medium text-base text-mk-ink tracking-[-0.01em]',
    description: 'text-mk-body text-sm/relaxed',
    button: 'default',
  },
  inline: {
    root: 'gap-0.5 px-3 py-5',
    copy: 'gap-0.5',
    title: 'font-medium text-mk-ink text-row',
    description: 'text-mk-body text-xs',
    button: null,
  },
} as const satisfies Record<
  EmptyStateSize,
  {
    root: string
    copy: string
    title: string
    description: string
    button: 'lg' | 'default' | null
  }
>

export function EmptyState(props: EmptyStateProps) {
  const size = props.size ?? 'panel'
  const reason = props.reason ?? 'empty'
  const styles = SIZE[size]
  const block = props.size === 'inline' ? null : props

  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-col items-center justify-center text-balance text-center',
        styles.root,
        props.className
      )}
      data-reason={reason}
      data-size={size}
      data-slot="empty-state"
    >
      {props.size === 'page' ? <PageVisualSlot {...props} /> : null}
      {block?.kicker ? (
        <EmptyStateKicker done={reason === 'done'}>
          {block.kicker}
        </EmptyStateKicker>
      ) : null}
      <div className={cn('flex flex-col items-center', styles.copy)}>
        {/* A page state is the route's content, so its title is a heading;
            smaller sizes sit under a section that already has one. */}
        {size === 'page' ? (
          <h2 className={styles.title}>{props.title}</h2>
        ) : (
          <p className={styles.title}>{props.title}</p>
        )}
        {props.description ? (
          <p className={styles.description}>{props.description}</p>
        ) : null}
      </div>
      {block?.children}
      {styles.button === null ? (
        <InlineAction action={props.action} />
      ) : (
        <EmptyStateActions
          action={block?.action}
          buttonSize={styles.button}
          pairPills={size === 'page' && reason === 'empty'}
          reason={reason}
          secondaryAction={block?.secondaryAction}
        />
      )}
    </div>
  )
}

function InlineAction({ action }: { action?: EmptyStateAction }) {
  return action ? <EmptyStateLink action={action} tone="accent" /> : null
}

/**
 * The primary action is the brand pill for a first run and an outline pill
 * otherwise. Only a first-run page pairs two pills (brand + ring); every
 * other secondary is a quiet ink link.
 */
function EmptyStateActions({
  action,
  secondaryAction,
  reason,
  buttonSize,
  pairPills,
}: {
  action?: EmptyStateAction
  secondaryAction?: EmptyStateAction
  reason: EmptyStateReason
  buttonSize: 'lg' | 'default'
  pairPills: boolean
}) {
  if (!(action || secondaryAction)) {
    return null
  }
  let secondary: ReactNode = null
  if (secondaryAction) {
    secondary =
      action && pairPills ? (
        <EmptyStateButton
          action={secondaryAction}
          size={buttonSize}
          variant="outline"
        />
      ) : (
        <EmptyStateLink action={secondaryAction} tone="ink" />
      )
  }
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {action ? (
        <EmptyStateButton
          action={action}
          size={buttonSize}
          variant={reason === 'empty' ? 'default' : 'outline'}
        />
      ) : null}
      {secondary}
    </div>
  )
}

const FRAME =
  'relative isolate flex min-h-[232px] w-full max-w-[600px] items-center justify-center overflow-hidden rounded-3xl p-6 shadow-[0_0_0_1px_rgb(0_0_0/0.04)]'

// The sky (shader, prompt timer, chat icons) loads only on a page that has
// prompts; until it arrives the frame holds its place in the brand tint.
const EmptyStateSky = dynamic(
  () => import('./empty-state-sky').then((module) => module.EmptyStateSky),
  { loading: () => <div className={cn(FRAME, 'bg-mk-peach')} /> }
)

function PageVisualSlot(props: PageVisual) {
  if (props.media) {
    return <div className={cn(FRAME, 'bg-mk-tile')}>{props.media}</div>
  }
  if (props.prompts?.length) {
    return (
      <EmptyStateSky
        className={FRAME}
        onPrompt={props.onPrompt}
        prompts={props.prompts}
      />
    )
  }
  return null
}

function EmptyStateKicker({
  done,
  children,
}: {
  done: boolean
  children: ReactNode
}) {
  return done ? (
    <p className="flex items-center gap-1.5 rounded-full bg-mk-card px-2.5 py-1 text-mk-body text-xs shadow-[0_0_0_1px_var(--mk-ring)]">
      <span
        aria-hidden="true"
        className="size-1.5 rounded-full bg-mk-success"
      />
      {children}
    </p>
  ) : (
    <p className={TYPE.kicker}>{children}</p>
  )
}

function EmptyStateButton({
  action,
  size,
  variant,
}: {
  action: EmptyStateAction
  size: 'lg' | 'default'
  variant: 'default' | 'outline'
}) {
  return action.href ? (
    <Button asChild size={size} variant={variant}>
      <IntentPrefetchLink href={action.href}>{action.label}</IntentPrefetchLink>
    </Button>
  ) : (
    <Button
      disabled={action.disabled}
      onClick={action.onClick}
      size={size}
      title={action.disabled ? action.disabledReason : undefined}
      type="button"
      variant={variant}
    >
      {action.label}
    </Button>
  )
}

/** `ink` is the quiet secondary beside a page/panel; `accent` is the one
 * inline link (mk-accent-text clears 4.5:1 at 12px). */
function EmptyStateLink({
  action,
  tone,
}: {
  action: EmptyStateAction
  tone: 'ink' | 'accent'
}) {
  const className = cn(
    'flex items-center font-medium underline-offset-4 hover:underline',
    tone === 'ink'
      ? 'h-9 px-2 text-mk-ink text-sm'
      : 'pt-0.5 text-mk-accent-text text-xs'
  )
  return action.href ? (
    <IntentPrefetchLink className={className} href={action.href}>
      {action.label}
    </IntentPrefetchLink>
  ) : (
    <button
      className={cn(
        className,
        'disabled:pointer-events-none disabled:opacity-50'
      )}
      disabled={action.disabled}
      onClick={action.onClick}
      title={action.disabled ? action.disabledReason : undefined}
      type="button"
    >
      {action.label}
    </button>
  )
}
